/**
 * Typed client for the SENPAI backend API. Single source of truth for the
 * request/response shapes the components consume — keeps the screens
 * focused on UI, not network details.
 *
 * Base URL is baked into the bundle from VITE_API_BASE_URL at build time
 * (set as a GH Actions secret; see scripts/setup-github.sh).
 */

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

if (!BASE) {
  // eslint-disable-next-line no-console
  console.warn('VITE_API_BASE_URL is not set; API calls will fail.');
}

// ── Shapes ──────────────────────────────────────────────────────────────────

export interface AgentVoice {
  tone?: string[];
  register?: string;
  signature_phrases?: string[];
  language?: string;
}

export interface AgentMatchingHints {
  interests?: string[];
  user_profile_fit?: string[];
}

export interface Agent {
  id: string;
  type: 'cohort' | 'persona' | 'synthetic';
  display_name: string;
  headline: string;
  avatar_image?: string;
  voice?: AgentVoice;
  matching_hints?: AgentMatchingHints;
  disclosure?: { is_real?: boolean; ui_label?: string };
  identity?: Record<string, unknown>;
}

export type CandidatePhase = 'going' | 'deciding' | 'returned' | 'family';

export interface MatchInput {
  interests: string[];
  phase: CandidatePhase;
  free_text?: string;
}

export interface MatchResult {
  agent_id: string;
  display_name: string;
  headline: string;
  avatar_image?: string;
  type: string;
  score: number;
  score_breakdown: { total: number; tag: number; phase: number; freetext: number };
  reason: string;
}

export interface ChatCitation {
  log_id: string;
  day_label?: string;
  source_url?: string;
}

export interface ChatRequest {
  agent_id: string;
  session_id?: string;
  message: string;
}

export interface ChatResponse {
  session_id: string;
  reply: string;
  citations: ChatCitation[];
}

// ── Trip log (alumni surface) ───────────────────────────────────────────────

export type LogPhase = 'before' | 'during' | 'after';

export interface SenpaiLog {
  id: string;
  senpai_id: string;
  visit_cohort?: string;
  phase: LogPhase;
  day_label?: string;
  date_approx?: string;
  tags?: string[];
  takeaways?: string[];
  emotion_curve?: string[];
  body_markdown: string;
  source_urls?: string[];
  provenance?: string;
}

export interface LogStructureRequest {
  senpai_id: string;
  prompts: Array<{ question: string; answer: string }>;
  phase?: LogPhase;
  day_label?: string;
}

export interface InterviewNextRequest {
  senpai_id: string;
  phase: LogPhase;
  prior_qa: Array<{ question: string; answer: string }>;
  day_label?: string;
}

export interface InterviewNextResponse {
  question: string;
  done: boolean;
  turn: number;
}

// ── Calls ───────────────────────────────────────────────────────────────────

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!r.ok) {
    let detail: unknown;
    try {
      detail = await r.json();
    } catch {
      detail = await r.text();
    }
    throw new ApiError(r.status, path, detail);
  }
  return (await r.json()) as T;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly path: string, public readonly detail: unknown) {
    super(`API ${status} on ${path}`);
  }

  /** True if the backend explicitly marked the failure as retryable. */
  get retryable(): boolean {
    if (typeof this.detail === 'object' && this.detail !== null && 'retryable' in this.detail) {
      return (this.detail as { retryable?: unknown }).retryable === true;
    }
    return this.status === 502 || this.status === 503 || this.status === 504;
  }

  /** User-facing message from the backend, falls back to the generic Error message. */
  get userMessage(): string {
    if (typeof this.detail === 'object' && this.detail !== null && 'message' in this.detail) {
      const m = (this.detail as { message?: unknown }).message;
      if (typeof m === 'string') return m;
    }
    return this.message;
  }
}

export function listAgents(): Promise<{ agents: Agent[]; count: number }> {
  return request('/agents');
}

export function getAgent(agentId: string): Promise<Agent> {
  return request(`/agents/${encodeURIComponent(agentId)}`);
}

export function match(input: MatchInput): Promise<{ matches: MatchResult[]; topK: number }> {
  return request('/match', { method: 'POST', body: JSON.stringify(input) });
}

export function chat(req: ChatRequest): Promise<ChatResponse> {
  return request('/chat', { method: 'POST', body: JSON.stringify(req) });
}

export function listLogs(senpaiId: string): Promise<{
  senpai_id: string;
  count: number;
  logs: SenpaiLog[];
}> {
  return request(`/log/${encodeURIComponent(senpaiId)}`);
}

export function structureLog(req: LogStructureRequest): Promise<{ draft: SenpaiLog }> {
  return request('/log/structure', { method: 'POST', body: JSON.stringify(req) });
}

export function interviewNext(req: InterviewNextRequest): Promise<InterviewNextResponse> {
  return request('/log/interview/next', { method: 'POST', body: JSON.stringify(req) });
}

export interface TranscribeRequest {
  audio_base64: string;
  mime_type: string;
  language?: string;
}

export interface TranscribeResponse {
  text: string;
}

export function transcribeAudio(req: TranscribeRequest): Promise<TranscribeResponse> {
  return request('/transcribe', { method: 'POST', body: JSON.stringify(req) });
}

export function commitLog(draft: SenpaiLog): Promise<{ ok: true; id: string }> {
  return request('/log/commit', { method: 'POST', body: JSON.stringify(draft) });
}

export function deleteLog(senpaiId: string, logId: string): Promise<{ ok: true }> {
  return request(
    `/log/${encodeURIComponent(senpaiId)}/${encodeURIComponent(logId)}`,
    { method: 'DELETE' },
  );
}

// ── Operator dashboard ──────────────────────────────────────────────────────

export interface DashboardTopic {
  tag: string;
  count: number;
}

export interface DashboardSenpai {
  agent_id: string;
  display_name: string;
  count: number;
}

export interface DashboardWeekly {
  new_logs: number;
  new_plans: number;
  new_reflections: number;
  active_sessions: number;
}

export interface DashboardData {
  period: string;
  generated_at: string;
  topics: DashboardTopic[];
  senpai_popularity: DashboardSenpai[];
  new_alumni_logs: number;
  weekly?: DashboardWeekly;
  tuning_hint: string;
  data_note: string;
}

export function getDashboard(): Promise<DashboardData> {
  return request('/dashboard');
}

// ── Recommended plan (Wrap screen) ──────────────────────────────────────────

export interface RecommendPlanItem {
  title: string;
  description: string;
  why: string;
}

export interface RecommendPlanResponse {
  headline: string;
  summary: string;
  items: RecommendPlanItem[];
  expected_effects: string[];
}

export interface RecommendRequest {
  agent_id: string;
  turns: Array<{ role: 'user' | 'assistant'; content: string }>;
  interests?: string[];
  phase?: CandidatePhase;
  free_text?: string;
}

export function recommendPlan(req: RecommendRequest): Promise<RecommendPlanResponse> {
  return request('/recommend', { method: 'POST', body: JSON.stringify(req) });
}

// ── Image anonymizer ────────────────────────────────────────────────────────

export interface ImageAnonymizeRequest {
  image_base64: string;
  mime_type: string;
  hint?: string;
}

export interface ImageAnonymizeResponse {
  description: string;
  illustration_b64: string;
  illustration_mime: string;
}

export function anonymizeImage(req: ImageAnonymizeRequest): Promise<ImageAnonymizeResponse> {
  return request('/image/anonymize', { method: 'POST', body: JSON.stringify(req) });
}
